import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, MessageCircle, Send, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: 'algarve',
    level: '',
    group_size: '',
    preferred_dates: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        destination: 'algarve',
        level: '',
        group_size: '',
        preferred_dates: '',
        message: '',
      });
    } catch (err) {
      setError('Something went wrong. Please try again or contact us directly.');
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const whatsappNumber = '+351968382742';
  const whatsappMessage = 'Hello! I\'m interested in booking a padel holiday in the Algarve.';
  const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="bg-[#f5f3ee]" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden pt-24" data-testid="contact-hero">
        <div className="absolute inset-0 bg-[#414b3b]" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center py-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-5 py-2 border border-[#8ca87c] text-[#8ca87c] text-xs tracking-[0.3em] uppercase mb-8">
              Get in Touch
            </span>
            <h1 className="font-heading font-light text-4xl sm:text-5xl md:text-6xl text-[#f5f3ee] mb-6">
              Book Your Trip
            </h1>
            <p className="text-lg text-[#f5f3ee]/70 max-w-xl mx-auto font-light">
              Ready to experience the best padel holiday in the Algarve? 
              Fill out the form and we'll get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 md:py-32" data-testid="contact-form-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <motion.div {...fadeInUp}>
              {isSubmitted ? (
                <div className="bg-[#8ca87c]/20 p-8 md:p-12 text-center" data-testid="success-message">
                  <div className="w-20 h-20 bg-[#8ca87c] flex items-center justify-center mx-auto mb-6">
                    <Check size={40} className="text-[#f5f3ee]" />
                  </div>
                  <h3 className="font-heading text-2xl text-[#414b3b] mb-4">
                    Thank You!
                  </h3>
                  <p className="text-[#414b3b]/60 font-light mb-6">
                    We've received your inquiry and will get back to you within 24 hours. 
                    Check your email for a confirmation.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="h-12 px-8 bg-[#414b3b] text-[#f5f3ee] font-light tracking-[0.1em] uppercase text-sm transition-all duration-300 hover:bg-[#8ca87c]"
                    data-testid="submit-another-btn"
                  >
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#414b3b] font-light tracking-wider uppercase text-xs">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="h-12 bg-white border-[#414b3b]/20 focus:border-[#8ca87c] focus:ring-[#8ca87c]"
                        data-testid="input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#414b3b] font-light tracking-wider uppercase text-xs">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="h-12 bg-white border-[#414b3b]/20 focus:border-[#8ca87c] focus:ring-[#8ca87c]"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#414b3b] font-light tracking-wider uppercase text-xs">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+1 234 567 890"
                        value={formData.phone}
                        onChange={handleChange}
                        className="h-12 bg-white border-[#414b3b]/20 focus:border-[#8ca87c] focus:ring-[#8ca87c]"
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="destination" className="text-[#414b3b] font-light tracking-wider uppercase text-xs">
                        Destination
                      </Label>
                      <Select
                        value={formData.destination}
                        onValueChange={(value) => handleSelectChange('destination', value)}
                      >
                        <SelectTrigger className="h-12 bg-white border-[#414b3b]/20" data-testid="select-destination">
                          <SelectValue placeholder="Select destination" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="algarve">Algarve, Portugal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="level" className="text-[#414b3b] font-light tracking-wider uppercase text-xs">
                        Your Padel Level
                      </Label>
                      <Select
                        value={formData.level}
                        onValueChange={(value) => handleSelectChange('level', value)}
                      >
                        <SelectTrigger className="h-12 bg-white border-[#414b3b]/20" data-testid="select-level">
                          <SelectValue placeholder="Select your level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                          <SelectItem value="pro">Professional</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="group_size" className="text-[#414b3b] font-light tracking-wider uppercase text-xs">
                        Group Size
                      </Label>
                      <Select
                        value={formData.group_size}
                        onValueChange={(value) => handleSelectChange('group_size', value)}
                      >
                        <SelectTrigger className="h-12 bg-white border-[#414b3b]/20" data-testid="select-group">
                          <SelectValue placeholder="How many people?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Just me</SelectItem>
                          <SelectItem value="2">2 people</SelectItem>
                          <SelectItem value="3-4">3-4 people</SelectItem>
                          <SelectItem value="5+">5+ people</SelectItem>
                          <SelectItem value="corporate">Corporate group</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferred_dates" className="text-[#414b3b] font-light tracking-wider uppercase text-xs">
                      Preferred Dates
                    </Label>
                    <Input
                      id="preferred_dates"
                      name="preferred_dates"
                      type="text"
                      placeholder="e.g., March 15-22, 2025 or 'Flexible'"
                      value={formData.preferred_dates}
                      onChange={handleChange}
                      className="h-12 bg-white border-[#414b3b]/20 focus:border-[#8ca87c] focus:ring-[#8ca87c]"
                      data-testid="input-dates"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#414b3b] font-light tracking-wider uppercase text-xs">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your ideal padel holiday, any special requests, or questions you have..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="bg-white border-[#414b3b]/20 focus:border-[#8ca87c] focus:ring-[#8ca87c] resize-none"
                      data-testid="input-message"
                    />
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 text-red-600 text-sm" data-testid="form-error">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-[#8ca87c] text-[#f5f3ee] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center gap-3 transition-all duration-300 hover:bg-[#414b3b] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send size={18} />
                        Send Inquiry
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-[#414b3b] p-8 md:p-10 mb-8">
                <h3 className="font-heading text-2xl text-[#f5f3ee] mb-6">
                  Contact Information
                </h3>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#f5f3ee]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#8ca87c]" size={20} />
                    </div>
                    <div>
                      <p className="text-[#f5f3ee] font-light mb-1">Location</p>
                      <p className="text-[#f5f3ee]/60 font-light text-sm">Algarve, Portugal</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#f5f3ee]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#8ca87c]" size={20} />
                    </div>
                    <div>
                      <p className="text-[#f5f3ee] font-light mb-1">Email</p>
                      <a
                        href="mailto:padelholidaysalgarve@gmail.com"
                        className="text-[#f5f3ee]/60 hover:text-[#8ca87c] transition-colors font-light text-sm"
                        data-testid="contact-email-link"
                      >
                        padelholidaysalgarve@gmail.com
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#f5f3ee]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#8ca87c]" size={20} />
                    </div>
                    <div>
                      <p className="text-[#f5f3ee] font-light mb-1">Phone</p>
                      <a
                        href="tel:+351968382742"
                        className="text-[#f5f3ee]/60 hover:text-[#8ca87c] transition-colors font-light text-sm"
                        data-testid="contact-phone-link"
                      >
                        +351 968 382 742
                      </a>
                    </div>
                  </li>
                </ul>
              </div>

              {/* WhatsApp CTA */}
              <div className="bg-[#8ca87c] p-8 md:p-10 text-center">
                <div className="w-16 h-16 bg-[#414b3b] flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="text-[#f5f3ee]" size={28} />
                </div>
                <h3 className="font-heading text-2xl text-[#414b3b] mb-3">
                  Prefer WhatsApp?
                </h3>
                <p className="text-[#414b3b]/70 font-light mb-6">
                  Chat with us directly for quick responses and personalized assistance.
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-14 px-8 bg-[#414b3b] text-[#f5f3ee] font-light text-sm tracking-[0.15em] uppercase items-center justify-center gap-3 transition-all duration-300 hover:bg-[#333d2e]"
                  data-testid="whatsapp-cta-btn"
                >
                  <MessageCircle size={18} />
                  Chat on WhatsApp
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
      <section className="py-20 bg-white" data-testid="map-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-4">
              Find Us in the Algarve
            </h2>
            <p className="text-[#414b3b]/60 font-light">
              Our experiences take place across the beautiful Algarve region of Portugal.
            </p>
          </motion.div>
          <div className="overflow-hidden aspect-[21/9]">
            <img
              src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/0bqdaba0_WhatsApp%20Image%202026-02-07%20at%2012.44.21.jpeg"
              alt="Algarve aerial view"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
